import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import PersonIcon from '@mui/icons-material/Person';
import { updateProduction } from 'services/api/production';
// @mui material components
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Divider from '@mui/material/Divider';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';

/**
 * Dialog component for adding/removing responsibles
 */
function ResponsibleDialog({
    open,
    onClose,
    allResponsibles,
    productionID,
    aaroOrder,
    users,
    onUpdateSuccess,
    setOpen,
}) {
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const profilePictureStyle = {
        color: 'transparent',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        textAlign: 'center',
        textIndent: '10000px',
    };

    /**
     * Handle adding/removing a responsible user
     * @param {Object} user - User to add/remove as responsible
     */
    const handleUpdateResponsibles = async (user) => {
        if (!user?._id) return;
        if (!productionID) {
            console.error('Production ID is undefined');
            onClose();
            return;
        }

        setIsSubmitting(true);

        // Calculate the new responsibles list
        const currentResponsibles = allResponsibles?.filter(Boolean)?.map((responsible) => responsible._id) || [];
        const isExistingResponsible = currentResponsibles.includes(user._id);
        const newResponsibles = isExistingResponsible
            ? currentResponsibles.filter((id) => id !== user._id)
            : [...currentResponsibles, user._id];

        try {
            // Make the API call
            const result = await updateProduction(productionID, { responsibles: newResponsibles });
            console.log('Production update result:', result);

            // Invalidate and refetch the production query using the aaroOrder
            if (aaroOrder) {
                console.log('Invalidating query with aaroOrder:', aaroOrder);
                await queryClient.invalidateQueries({ queryKey: ['fetchProduction', aaroOrder] });
                await queryClient.refetchQueries({ queryKey: ['fetchProduction', aaroOrder] });
            } else {
                console.log('Invalidating query with productionID:', productionID);
                await queryClient.invalidateQueries({ queryKey: ['fetchProduction', productionID] });
            }

            // Invalidate related queries
            queryClient.invalidateQueries({ queryKey: ['searchProductions'] });

            // Call the callback if provided - this is crucial for updating the UI
            if (onUpdateSuccess) {
                console.log('Calling onUpdateSuccess callback');
                onUpdateSuccess();
            }

            setIsSubmitting(false);
            setOpen(false);
        } catch (error) {
            console.error('Failed to update responsibles:', error);
            queryClient.invalidateQueries({ queryKey: ['fetchProduction', aaroOrder || productionID] });
            setIsSubmitting(false);
        }
    };

    /**
     * Render user avatar with appropriate styling
     * @param {Object} user - User object
     * @returns {JSX.Element} Avatar component
     */
    const renderUserAvatar = (user) => {
        if (!user)
            return (
                <Avatar>
                    <PersonIcon />
                </Avatar>
            );

        return (
            <Avatar>
                {user.profilePicture ? (
                    <img src={user.profilePicture} alt={user.name || 'User'} style={profilePictureStyle} />
                ) : (
                    <PersonIcon />
                )}
            </Avatar>
        );
    };

    // Filter users based on search term
    const filteredUsers = (users || [])
        .filter((user) => user && user._id)
        .filter((user) => !searchTerm || (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <Dialog
            onClose={onClose}
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <MDBox p={3} display="flex" flexDirection="column" backgroundColor="white">
                <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h5" fontWeight="medium">
                        Üretime Sorumlu Ekle/Çıkar
                    </MDTypography>
                    <MDButton variant="text" color="dark" onClick={onClose} iconOnly circular>
                        <Icon>close</Icon>
                    </MDButton>
                </MDBox>

                <MDBox mb={3}>
                    <input
                        type="text"
                        placeholder="Kullanıcı ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            fontSize: '14px',
                        }}
                    />
                </MDBox>

                <Divider />

                <MDBox height="300px" sx={{ overflowY: 'auto' }}>
                    <List>
                        {filteredUsers.length === 0 ? (
                            <MDBox p={2} textAlign="center">
                                <MDTypography variant="body2" color="text">
                                    {searchTerm ? 'Sonuç bulunamadı' : 'Kullanıcı bulunamadı'}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            filteredUsers.map((user) => {
                                const isSelected = allResponsibles?.some((el) => el?._id === user._id);
                                return (
                                    <ListItem
                                        button
                                        onClick={() => !isSubmitting && handleUpdateResponsibles(user)}
                                        key={user._id}
                                        disabled={isSubmitting}
                                        sx={[
                                            {
                                                borderRadius: '8px',
                                                mb: 0.5,
                                            },
                                            isSelected
                                                ? {
                                                      backgroundColor: 'rgba(21, 101, 192, 0.1)',
                                                  }
                                                : {
                                                      backgroundColor: 'transparent',
                                                  },
                                            isSelected
                                                ? {
                                                      '&:hover': {
                                                          backgroundColor: 'rgba(21, 101, 192, 0.15)',
                                                      },
                                                  }
                                                : {
                                                      '&:hover': {
                                                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                      },
                                                  },
                                        ]}
                                    >
                                        <ListItemAvatar>
                                            {isSelected ? (
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={
                                                        <Icon fontSize="small" sx={{ color: '#1565C0' }}>
                                                            check_circle
                                                        </Icon>
                                                    }
                                                >
                                                    {renderUserAvatar(user)}
                                                </Badge>
                                            ) : (
                                                renderUserAvatar(user)
                                            )}
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight={isSelected ? 'medium' : 'regular'}
                                                >
                                                    {user?.name || 'İsimsiz Kullanıcı'}
                                                </MDTypography>
                                            }
                                        />
                                        {isSelected && (
                                            <Chip label="Sorumlu" size="small" color="info" sx={{ ml: 1 }} />
                                        )}
                                    </ListItem>
                                );
                            })
                        )}
                    </List>
                </MDBox>

                <MDBox mt={3} display="flex" justifyContent="flex-end">
                    <MDButton variant="contained" color="info" onClick={onClose}>
                        Tamamla
                    </MDButton>
                </MDBox>

                {isSubmitting && (
                    <Fade in={isSubmitting}>
                        <CircularProgress
                            size={24}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    </Fade>
                )}
            </MDBox>
        </Dialog>
    );
}

/**
 * Button component to add/edit responsibles
 */
function AddResponsible({
    allResponsibles,
    productionID,
    aaroOrder,
    users,
    // Handle additional props used in ProductionStatus
    showInfo,
    showAdd = true,
    showAvatar = true,
    isOpen,
    onClose,
    onUpdateSuccess,
    size,
}) {
    const [open, setOpen] = useState(isOpen || false);

    // Handle dialog close
    const handleClose = () => {
        setOpen(false);
        if (onClose) onClose();
    };

    // Update open state when isOpen prop changes
    useEffect(() => {
        if (isOpen !== undefined) {
            setOpen(isOpen);
        }
    }, [isOpen]);

    return (
        <>
            {showAdd && (
                <MDButton
                    variant="outlined"
                    color="dark"
                    size={size === 'xs' ? 'small' : 'small'}
                    circular
                    iconOnly
                    onClick={() => setOpen(true)}
                    sx={{
                        minWidth: size === 'xs' ? '24px' : '32px',
                        width: size === 'xs' ? '24px' : '32px',
                        height: size === 'xs' ? '24px' : '32px',
                        ml: 0.25,
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            borderColor: 'rgba(0, 0, 0, 0.2)',
                        },
                    }}
                >
                    <Icon sx={{ fontWeight: 'bold', fontSize: size === 'xs' ? '14px' : '16px' }}>add</Icon>
                </MDButton>
            )}
            <ResponsibleDialog
                open={open}
                onClose={handleClose}
                allResponsibles={allResponsibles}
                productionID={productionID}
                aaroOrder={aaroOrder}
                users={users}
                onUpdateSuccess={onUpdateSuccess}
                setOpen={setOpen}
            />
        </>
    );
}

// PropTypes for better documentation and validation
AddResponsible.propTypes = {
    allResponsibles: PropTypes.array.isRequired,
    productionID: PropTypes.string.isRequired,
    aaroOrder: PropTypes.string,
    users: PropTypes.array.isRequired,
    showInfo: PropTypes.bool,
    showAdd: PropTypes.bool,
    showAvatar: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onUpdateSuccess: PropTypes.func,
};

ResponsibleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    allResponsibles: PropTypes.array.isRequired,
    productionID: PropTypes.string.isRequired,
    aaroOrder: PropTypes.string,
    users: PropTypes.array.isRequired,
    onUpdateSuccess: PropTypes.func,
    setOpen: PropTypes.func.isRequired,
};

export default AddResponsible;
